import { useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useRequest, useSetState } from "ahooks";
import type { UploadFile, UploadProps } from "antd-v5";

import AppSetting from "config/AppSetting";
import reportCenterService from "service/reportCenter";
import {
  DATA_START_MONTH,
  DATA_START_QUARTER,
  DATA_START_YEAR,
  MAX_FILE_SIZE_BYTES,
  METRIC_OPTIONS,
  MONTH_OPTIONS,
  QUARTER_OPTIONS,
} from "../../constants";
import {
  buildYearOptions,
  formatAuditingPeriod,
  getCurrentQuarterPeriod,
  getDefaultMonth,
  getDefaultQuarter,
  isAcceptedFile,
  isMetricOne,
  isMetricThree,
} from "../../utils";
import type { DefaultMonthValue, DefaultQuarterValue, FieldErrors } from "../../types";

import {
  GetPmpMonitoringReportUploadResultResponse,
  UploadLogStatus,
  UploadPmpMonitoringReportFailStage,
  UploadPmpMonitoringReportResponse,
} from "service/reportCenter/types";

// Azure kills requests at 230s, so the save runs server-side in the background;
// we poll the upload-log row every 10s until it reaches a terminal status.
const POLL_INTERVAL_MS = 10_000;

// Consecutive poll failures tolerated before giving up; transient blips are retried.
const MAX_CONSECUTIVE_POLL_ERRORS = 10;

const useMonitoringReportUpload = () => {
  const [metric, setMetric] = useState(METRIC_OPTIONS[0].value);
  const [quarterPeriod, setQuarterPeriod] = useSetState<DefaultQuarterValue>(() =>
    getDefaultQuarter()
  );
  const [monthPeriod, setMonthPeriod] = useSetState<DefaultMonthValue>(() => getDefaultMonth());
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [fieldErrors, setFieldErrors] = useSetState<FieldErrors>({
    fileNotChosen: false,
    fileFormatInvalid: false,
    fileTooLarge: false,
    quarterNotSelected: false,
    monthNotSelected: false,
  });
  const [result, setResult] = useSetState<{
    uploaded: boolean;
    response: UploadPmpMonitoringReportResponse;
  }>({
    uploaded: false,
    response: {} as UploadPmpMonitoringReportResponse,
  });
  const [polling, setPolling] = useState(false);
  const pollErrorCountRef = useRef(0);

  const selectedFile = useMemo(() => {
    const originFile = uploadFileList[0]?.originFileObj;
    return originFile instanceof File ? originFile : null;
  }, [uploadFileList]);

  // Max allowed periods — computed once on mount from the current date.
  const maxMonthPeriod = useMemo(() => getDefaultMonth(), []);
  const maxLastQuarterPeriod = useMemo(() => getDefaultQuarter(), []);
  const maxCurrentQuarterPeriod = useMemo(() => getCurrentQuarterPeriod(), []);

  const maxQuarterPeriod = useMemo(
    () => (isMetricThree(metric) ? maxCurrentQuarterPeriod : maxLastQuarterPeriod),
    [metric, maxCurrentQuarterPeriod, maxLastQuarterPeriod]
  );

  const quarterYearOptions = useMemo(
    () => buildYearOptions(Number(maxQuarterPeriod.year)),
    [maxQuarterPeriod.year]
  );

  const monthYearOptions = useMemo(
    () => buildYearOptions(Number(maxMonthPeriod.year)),
    [maxMonthPeriod.year]
  );

  const availableQuarterOptions = useMemo(() => {
    let options = QUARTER_OPTIONS;
    if (quarterPeriod.year === DATA_START_YEAR) {
      options = options.filter((opt) => opt.value >= DATA_START_QUARTER);
    }
    if (quarterPeriod.year === maxQuarterPeriod.year) {
      options = options.filter((opt) => opt.value <= maxQuarterPeriod.quarter);
    }
    return options;
  }, [quarterPeriod.year, maxQuarterPeriod]);

  const availableMonthOptions = useMemo(() => {
    let options = MONTH_OPTIONS;
    if (monthPeriod.year === DATA_START_YEAR) {
      options = options.filter((opt) => opt.value >= DATA_START_MONTH);
    }
    if (monthPeriod.year === maxMonthPeriod.year) {
      options = options.filter((opt) => opt.value <= maxMonthPeriod.month);
    }
    return options;
  }, [monthPeriod.year, maxMonthPeriod]);

  const {
    run: runUpload,
    loading,
    cancel: cancelUpload,
  } = useRequest(reportCenterService.uploadPmpMonitoringReport, {
    manual: true,
    onBefore: () => {
      setResult({ uploaded: true });
    },
    onSuccess: (response) => {
      if (response.logId) {
        setPolling(true);
        runPoll({ logId: response.logId });
        return;
      }
      setResult({ response });
    },
    onError: () => {
      message.error("Upload failed due to a network or server error.");
      setResult({
        response: {
          success: false,
          messages: ["Upload failed due to a network or server error."],
          statusCode: 500,
          failStage: "System",
        },
      });
    },
  });

  const { run: runPoll, cancel: cancelPoll } = useRequest(
    reportCenterService.getPmpMonitoringReportUploadResult,
    {
      manual: true,
      pollingInterval: POLL_INTERVAL_MS,
      // Network blips during a poll tick are retried by the polling loop itself;
      // the loop ends on a terminal status, when the user closes the modal, or after
      // MAX_CONSECUTIVE_POLL_ERRORS consecutive failures.
      onSuccess: (poll: GetPmpMonitoringReportUploadResultResponse) => {
        pollErrorCountRef.current = 0;
        const isTerminal =
          poll.found &&
          (poll.status === UploadLogStatus.Success || poll.status === UploadLogStatus.Failed);
        if (!isTerminal) {
          return;
        }
        cancelPoll();
        setPolling(false);
        setResult({
          response: {
            success: poll.status === UploadLogStatus.Success,
            messages: poll.messages,
            statusCode: 200,
            failStage:
              poll.status === UploadLogStatus.Failed
                ? UploadPmpMonitoringReportFailStage.System
                : null,
          },
        });
      },
      onError: () => {
        pollErrorCountRef.current += 1;
        if (pollErrorCountRef.current < MAX_CONSECUTIVE_POLL_ERRORS) {
          // Transient blip — let the polling loop retry on the next tick.
          return;
        }
        cancelPoll();
        setPolling(false);
        pollErrorCountRef.current = 0;
        setResult({
          response: {
            success: false,
            messages: [
              "Unable to retrieve the upload result. Your file may still be processing in the background.",
            ],
            statusCode: 500,
            failStage: UploadPmpMonitoringReportFailStage.System,
          },
        });
      },
    }
  );

  const handleMetricChange = (value: string) => {
    setMetric(value);
    setQuarterPeriod(maxLastQuarterPeriod);
    setMonthPeriod(maxMonthPeriod);
    setFieldErrors({
      fileNotChosen: false,
      fileFormatInvalid: false,
      fileTooLarge: false,
      quarterNotSelected: false,
      monthNotSelected: false,
    });
  };

  const handleQuarterYearChange = (value: string) => {
    setQuarterPeriod({ year: value, quarter: "" });
    setFieldErrors({ quarterNotSelected: false });
  };

  const handleQuarterChange = (value: string) => {
    setQuarterPeriod({ quarter: value });
    setFieldErrors({ quarterNotSelected: false });
  };

  const handleMonthYearChange = (value: string) => {
    setMonthPeriod({ year: value, month: "" });
    setFieldErrors({ monthNotSelected: false });
  };

  const handleMonthChange = (value: string) => {
    setMonthPeriod({ month: value });
    setFieldErrors({ monthNotSelected: false });
  };

  const handleRemoveSelectedFile = () => {
    setUploadFileList([]);
    setFieldErrors({ fileNotChosen: false, fileFormatInvalid: false, fileTooLarge: false });
  };

  const handleBeforeUpload: UploadProps["beforeUpload"] = () => {
    return false;
  };

  const handleUploadChange: UploadProps["onChange"] = ({ fileList }) => {
    const latestFile = fileList[fileList.length - 1];
    const originFile = latestFile?.originFileObj;
    setUploadFileList(fileList.slice(-1));
    if (originFile instanceof File) {
      setFieldErrors({ fileNotChosen: false, fileFormatInvalid: false, fileTooLarge: false });
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;

    if (!isMetricOne(metric) && !quarterPeriod.quarter) {
      setFieldErrors({ quarterNotSelected: true });
      isValid = false;
    }
    if (isMetricOne(metric) && !monthPeriod.month) {
      setFieldErrors({ monthNotSelected: true });
      isValid = false;
    }
    if (!selectedFile) {
      setFieldErrors({ fileNotChosen: true, fileFormatInvalid: false, fileTooLarge: false });
      isValid = false;
    } else if (!isAcceptedFile(selectedFile.name)) {
      setFieldErrors({ fileFormatInvalid: true, fileNotChosen: false, fileTooLarge: false });
      isValid = false;
    } else if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setFieldErrors({ fileTooLarge: true, fileNotChosen: false, fileFormatInvalid: false });
      isValid = false;
    }

    return isValid;
  };

  const reset = () => {
    cancelPoll();
    setPolling(false);
    pollErrorCountRef.current = 0;
    cancelUpload();
    setMetric(METRIC_OPTIONS[0].value);
    setQuarterPeriod(getDefaultQuarter());
    setMonthPeriod(getDefaultMonth());
    setUploadFileList([]);
    setFieldErrors({
      fileNotChosen: false,
      fileFormatInvalid: false,
      fileTooLarge: false,
      quarterNotSelected: false,
      monthNotSelected: false,
    });
    setResult({ uploaded: false, response: {} as UploadPmpMonitoringReportResponse });
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    runUpload({
      metric,
      auditingPeriod: formatAuditingPeriod(metric, quarterPeriod, monthPeriod),
      file: selectedFile!,
      userId: AppSetting.localStorageValue.user.get().identityUserInfo.internalUserID ?? "",
    });
  };

  return {
    metric,
    quarterPeriod,
    monthPeriod,
    selectedFile,
    uploadFileList,
    fieldErrors,
    quarterYearOptions,
    monthYearOptions,
    availableQuarterOptions,
    availableMonthOptions,
    result,
    loading: loading || polling,
    handleMetricChange,
    handleQuarterYearChange,
    handleQuarterChange,
    handleMonthYearChange,
    handleMonthChange,
    handleRemoveSelectedFile,
    handleBeforeUpload,
    handleUploadChange,
    handleSubmit,
    reset,
  };
};

export default useMonitoringReportUpload;
